import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { usePlansForReactivation } from 'src/hooks/usePlansForReactivation';
import { useSubscription } from 'src/hooks/useSubscription';
import { useReactivationDetails } from 'src/hooks/useReactivationDetails';
import { ReactivationScenario } from 'src/services/reactivation.service';
import { ReactivationCampaignType } from 'src/services/reactivation.types';
import { getFullPlanObj } from 'src/lib/utils';
import { PaymentMethod } from '@stripe/stripe-js';
import { setPaymentProcessing } from 'src/store/payment';
import {
  setNoPaymentIntentAvailable as setNoPaymentIntentAvailableAction,
  setSelectedPlanId,
} from 'src/store/signup';
import {
  trackReactivationFailed,
  trackReactivationSubmitted,
  trackReactivationViewed,
} from 'src/util/track';
import { getThreeMonthStandardPlan } from 'src/pages/subscription-new/utils/plans.util';
import { USER_STATUS } from 'src/pages/constants';
import history from 'src/lib/history';
import {
  getTargetPlanId,
  processDirectReactivation,
  processNewSubscriptionReactivation,
} from '../utils/reactivationUtils';
import { getCampaignFromSession } from 'src/util/campaign.util';
import { DEFAULT_DISCOUNT_ROLLOVER_PERCENT } from '../constants';

export const useReactivationFlow = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth?.user);
  const { selectedPlanId, allPlansSorted, addonWeek, noIntentAvailable } =
    useAppSelector(state => state.signUp);
  const { campaignType, subscription: reactivationStoreSubscription } =
    useAppSelector(s => s.reactivation);

  const { isLoading: plansLoading } = usePlansForReactivation();
  const { isSubscriptionLoading } = useSubscription(user);
  const {
    scenario,
    subscription,
    paymentMethod: initialPaymentMethod,
    shouldOnlyReactivateSubscription,
    isLoading: reactivationLoading,
  } = useReactivationDetails();

  const [paymentMethod, setPaymentMethod] = useState<
    PaymentMethod | undefined
  >();
  const [showPlanPopup, setShowPlanPopup] = useState(false);
  const [showPlanSelectModal, setShowPlanSelectModal] = useState(false);
  const walletPaymentIntentIdRef = useRef('');
  const setWalletPaymentIntentId = useCallback((id: string) => {
    walletPaymentIntentIdRef.current = id;
  }, []);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasTrackedPageView = useRef(false);
  const hasPromptedPlanSelect = useRef(false);

  const isReactivateNavigation =
    user?.status === USER_STATUS.ONHOLD || user?.status === USER_STATUS.INITIAL;

  const isLoading =
    plansLoading || isSubscriptionLoading || reactivationLoading;

  useEffect(() => {
    dispatch(setPaymentProcessing(false));
  }, [dispatch]);

  useEffect(() => {
    if (!isLoading && isReactivateNavigation) {
      history.replace('/account/subscription');
    }
  }, [isLoading, isReactivateNavigation]);

  useEffect(() => {
    if (!isLoading && !hasTrackedPageView.current) {
      hasTrackedPageView.current = true;
      trackReactivationViewed(
        'page',
        scenario || undefined,
        selectedPlanId,
        Boolean(initialPaymentMethod || paymentMethod)
      );
    }
  }, [
    isLoading,
    scenario,
    selectedPlanId,
    initialPaymentMethod,
    paymentMethod,
  ]);

  useEffect(() => {
    if (initialPaymentMethod) {
      setPaymentMethod(initialPaymentMethod);
    }
  }, [initialPaymentMethod]);

  // Pre-select plan matching backend subscription planId/priceId/intervalCount if available.
  // Falls back to 3-month Standard when no plan matches (e.g. stale/unknown planId).
  useEffect(() => {
    if (subscription && allPlansSorted.length > 0 && !selectedPlanId) {
      const targetId =
        getTargetPlanId(subscription, allPlansSorted) ||
        getThreeMonthStandardPlan(allPlansSorted).planId;
      if (targetId) {
        dispatch(setSelectedPlanId(targetId));
      }
    }
  }, [subscription, allPlansSorted, selectedPlanId, dispatch]);

  // Silently pre-select the default 3-month standard plan for users with no
  // subscription and no payment method, instead of showing the modal upfront.
  // The user can still change the plan via the "Change" button.
  useEffect(() => {
    if (
      scenario === ReactivationScenario.NO_SUBSCRIPTION_NO_PAYMENT &&
      !hasPromptedPlanSelect.current &&
      allPlansSorted.length > 0 &&
      !selectedPlanId
    ) {
      hasPromptedPlanSelect.current = true;
      const { planId } = getThreeMonthStandardPlan(allPlansSorted);
      if (planId) {
        dispatch(setSelectedPlanId(planId));
      }
    }
  }, [scenario, allPlansSorted, selectedPlanId, dispatch]);

  const effectivePlanObj = useMemo(() => {
    return getFullPlanObj(allPlansSorted, selectedPlanId, addonWeek);
  }, [allPlansSorted, selectedPlanId, addonWeek]);

  const priceId =
    effectivePlanObj?.id ||
    subscription?.priceId ||
    (selectedPlanId?.startsWith('price_') ? selectedPlanId : '');
  const productId =
    effectivePlanObj?.productId || subscription?.planId || 'prod_default';

  // Compute the amount that will actually be charged today, which differs per
  // campaign:
  //  - FREE_MONTH: always $0 (subscription starts but first charge is deferred)
  //  - DISCOUNT_ROLLOVER: the discounted price.
  //      On the initial plan use subscription.totalPrice (from BE);
  //      on a changed plan apply percent_off to the new plan's unit amount.
  //  - All other flows: the plan's full unit amount (existing behaviour).
  const totalPrice = useMemo(() => {
    const fullUnitAmount = effectivePlanObj?.unitAmount;

    if (campaignType === ReactivationCampaignType.FREE_MONTH) {
      return '0';
    }

    if (campaignType === ReactivationCampaignType.DISCOUNT_ROLLOVER) {
      const percentOff =
        reactivationStoreSubscription?.invoiceDiscount?.percent_off ??
        DEFAULT_DISCOUNT_ROLLOVER_PERCENT;
      const isInitialPlan =
        selectedPlanId === reactivationStoreSubscription?.planId ||
        selectedPlanId === reactivationStoreSubscription?.priceId;

      if (isInitialPlan && reactivationStoreSubscription?.totalPrice != null) {
        return String(reactivationStoreSubscription.totalPrice);
      }
      if (percentOff != null && fullUnitAmount != null) {
        const discounted = Number(fullUnitAmount) * (1 - percentOff / 100);
        return discounted.toFixed(2);
      }
    }

    return String(fullUnitAmount ?? subscription?.totalPrice ?? '0');
  }, [
    campaignType,
    effectivePlanObj,
    selectedPlanId,
    reactivationStoreSubscription,
    subscription,
  ]);

  const handleReactivate = useCallback(
    async (customPm?: PaymentMethod) => {
      const pmToUse = customPm || paymentMethod || initialPaymentMethod;
      if (!user || !pmToUse) {
        setErrorMessage('Payment method is required');
        return;
      }

      setIsSubmitting(true);
      dispatch(setPaymentProcessing(true));
      setErrorMessage('');

      const pmType = pmToUse.type || 'card';
      const totalPriceStr = totalPrice.toString();

      trackReactivationSubmitted(
        'page',
        pmType,
        selectedPlanId,
        totalPriceStr,
        scenario || undefined
      );

      try {
        if (shouldOnlyReactivateSubscription) {
          await processDirectReactivation({
            user,
            pmType,
            selectedPlanId,
            totalPrice: totalPriceStr,
            scenario,
            priceId,
            dispatch,
            campaignCode: getCampaignFromSession(),
          });
        } else {
          if (!priceId) {
            setErrorMessage('Please select a subscription plan');
            setIsSubmitting(false);
            dispatch(setPaymentProcessing(false));
            return;
          }

          const errorMsg = await processNewSubscriptionReactivation({
            user,
            paymentMethod: pmToUse,
            priceId,
            productId,
            totalPrice: totalPriceStr,
            walletPaymentIntentId: walletPaymentIntentIdRef.current,
            noIntentAvailable,
            selectedPlanId,
            allPlansSorted,
            effectivePlanObj: effectivePlanObj || undefined,
            pmType,
            scenario,
            dispatch,
            campaignCode: getCampaignFromSession(),
          });

          if (errorMsg) {
            setErrorMessage(errorMsg);
          }
        }
      } catch (err) {
        const error = err as any;
        const errorMsg =
          error?.response?.data?.message ||
          error?.message ||
          'Reactivation failed. Please try again.';
        trackReactivationFailed(
          'page',
          pmType,
          errorMsg,
          selectedPlanId,
          shouldOnlyReactivateSubscription
            ? 'cancel_reactivate'
            : 'startSubscription'
        );
        setErrorMessage(errorMsg);
      } finally {
        setIsSubmitting(false);
        dispatch(setPaymentProcessing(false));
      }
    },
    [
      user,
      paymentMethod,
      initialPaymentMethod,
      shouldOnlyReactivateSubscription,
      priceId,
      productId,
      totalPrice,
      noIntentAvailable,
      selectedPlanId,
      allPlansSorted,
      effectivePlanObj,
      scenario,
      dispatch,
    ]
  );

  const handlePaymentFormSubmit = useCallback(
    async (customPm?: PaymentMethod) => {
      const pmToUse = customPm || paymentMethod;
      if (!user || !pmToUse) {
        setErrorMessage('Please enter payment details');
        return;
      }
      await handleReactivate(pmToUse);
    },
    [user, paymentMethod, handleReactivate]
  );

  const setNoPaymentIntentAvailable = useCallback(
    (val: boolean) => dispatch(setNoPaymentIntentAvailableAction(val)),
    [dispatch]
  );

  const hasCC = Boolean(initialPaymentMethod);

  return {
    user,
    scenario,
    subscription,
    selectedPlanId,
    priceId,
    productId,
    totalPrice,
    paymentMethod,
    initialPaymentMethod,
    shouldOnlyReactivateSubscription,
    hasCC,
    isLoading: isLoading || isReactivateNavigation,
    isSubmitting,
    errorMessage,
    showPlanPopup,
    showPlanSelectModal,
    noIntentAvailable,
    setPaymentMethod,
    setErrorMessage,
    setShowPlanPopup,
    setShowPlanSelectModal,
    setWalletPaymentIntentId,
    setNoPaymentIntentAvailable,
    handleReactivate,
    handlePaymentFormSubmit,
  };
};
